<template>
  <Alert
    type="info"
    class="mb-6 md:absolute md:top-8 md:inset-x-8 z-10"
    v-if="showInviteParentAlert && !publicFundraiserData"
    >Sign up to help {{ inviteRequester?.firstName }}
    {{ inviteRequester?.lastName }} with their fundraiser.
  </Alert>

  <Alert
    type="info"
    class="mb-6 md:absolute md:top-8 md:inset-x-8 z-10"
    v-if="showInviteParentAlert && publicFundraiserData"
    >Sign up to help {{ inviteRequester?.firstName }}
    {{ inviteRequester?.lastName }} with their
    {{ publicFundraiserData.name }} fundraiser.
  </Alert>

  <Alert
    type="info"
    class="mb-6 md:absolute md:top-8 md:inset-x-8 z-10"
    v-if="showInviteUserAlert"
    >You have an invite to Snap! Mobile. Please sign up to accept it.
  </Alert>

  <Alert
    type="info"
    class="mb-6 md:absolute md:top-8 md:inset-x-8 z-10"
    v-if="showManageAlert"
  >
    <p>
      Snap! Manage has been upgraded to provide enhanced functionality and
      improved security. Please create an account to continue.
    </p>
    <br />
    <p v-if="manageRegistrationLink">
      <InlineLink :href="manageRegistrationLink" size="sm" :target="'_blank'"
        >Click Here to Browse Camps.
      </InlineLink>
    </p>
  </Alert>

  <Modal
    v-if="'parent_modal' in this.$route.query"
    wideCloseButton="true"
    type="warning"
  >
    <p class="p-4">
      If you received an email/text invite, you must use the same email address
      or phone number to create your account.
    </p></Modal
  >

  <PageTitle class="mb-3 md:mb-4">Create Your Snap! Mobile Account</PageTitle>

  <p class="mb-4 text-sm" v-if="featureOn?.('USER-888-new-sign-up-view')">
    Have an account?
    <Link :to="{ name: 'login' }" size="sm">Sign in</Link>
  </p>

  <section v-if="showGoogleSignIn">
    <div class="social-login">
      <GoogleSignInButton />
    </div>

    <div class="divider">
      <span>or</span>
    </div>
  </section>

  <RegistrationForm
    class="pt-4"
    @form:error="onFormError"
    :prefill="prefillRegistrationData"
  ></RegistrationForm>

  <p
    class="mt-10 text-right md:text-center text-sm"
    v-if="!featureOn?.('USER-888-new-sign-up-view')"
  >
    Already have Snap! Mobile account?
    <Link :to="{ name: 'login' }" size="sm">Sign in here.</Link>
  </p>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component";
import { mapGetters } from "vuex";

import Alert from "@/components/snap-ui/Alert.vue";
import Modal from "@/components/snap-ui/Modal.vue";
import InlineLink from "@/components/ui/InlineLink.vue";
import Link from "@/components/snap-ui/Link.vue";
import PageTitle from "@/components/external/PageTitle.vue";
import RegistrationForm from "@/components/RegistrationForm.vue";
import GoogleSignInButton from "@/components/ui/GoogleSignIn.vue";

import {
  PublicFundraiserData,
  User,
  UserOccupation,
  UserInviteParams,
} from "@/types/graphql";

import PublicFundraiserDataQuery from "@/graphql/publicFundraiserData.query.graphql";
import { FeatureToggle } from "@/store/treatments.module";
import { getPrePopulatedOccupation } from "@/services/occupation";
import { manageAlertLink } from "@/util/helper";

@Options<Registration>({
  components: {
    Alert,
    Modal,
    InlineLink,
    Link,
    PageTitle,
    RegistrationForm,
    GoogleSignInButton,
  },

  computed: {
    ...mapGetters([
      "consumer",
      "featureOn",
      "inviteEmail",
      "invitePhoneNumber",
      "inviteFirstName",
      "inviteLastName",
      "inviteType",
      "inviteRequester",
      "inviteParams",
      "joincode",
      "redirectTo",
    ]),
  },

  apollo: {
    publicFundraiserData: {
      query: PublicFundraiserDataQuery,
      variables() {
        return {
          identifier: this.joincode,
        };
      },
      skip() {
        return !this.joincode;
      },
    },
  },
})
export default class Registration extends Vue {
  consumer?: string;
  featureOn?: FeatureToggle;
  inviteEmail?: string;
  invitePhoneNumber?: string;
  inviteFirstName?: string;
  inviteLastName?: string;
  inviteType?: string;
  inviteRequester?: User;
  inviteParams?: UserInviteParams;
  joincode?: string;
  redirectTo?: string;

  publicFundraiserData?: PublicFundraiserData;

  prefillRegistrationData: {
    email?: string;
    phoneNumber?: string;
    firstName?: string;
    lastName?: string;
    occupation?: UserOccupation;
  } = {};

  hasError = false;

  async mounted() {
    await this.$store.dispatch("useTreatments");

    if (this.featureOn?.("USER-569-role-selector-pre-populate")) {
      const occupation = getPrePopulatedOccupation(
        this.consumer,
        this.redirectTo
      );

      if (occupation) this.prefillRegistrationData.occupation = occupation;
    }

    if (this.inviteEmail) {
      this.prefillRegistrationData.email = this.inviteEmail;
      this.prefillRegistrationData.occupation = this.inviteParams
        ?.occupation as UserOccupation;
    }

    if (this.inviteParams?.firstName) {
      this.prefillRegistrationData.firstName = this.inviteParams?.firstName;
    }

    if (this.inviteParams?.lastName) {
      this.prefillRegistrationData.lastName = this.inviteParams?.lastName;
    }

    if (this.invitePhoneNumber) {
      this.prefillRegistrationData.phoneNumber = this.invitePhoneNumber;
      this.prefillRegistrationData.occupation = this.inviteParams
        ?.occupation as UserOccupation;
    }
  }

  get showGoogleSignIn(): boolean {
    return (
      this.featureOn?.("USER-1079-sign-in-with-google") ||
      this.$route.query.preview !== undefined
    );
  }

  get showInviteParentAlert(): boolean {
    return !this.hasError && this.inviteType === "parent";
  }

  get showInviteUserAlert(): boolean {
    return !this.hasError && !!this.inviteEmail && !this.joincode;
  }

  get showManageAlert(): boolean {
    return !this.hasError && this.consumer === "manage";
  }

  get manageRegistrationLink(): string | undefined {
    return manageAlertLink(this.consumer, this.redirectTo);
  }

  onFormError() {
    this.hasError = true;
  }
}
</script>
